// Locale utilities and helpers for O-Ton multilingual content

import type { Locale, Localized } from "@/types/content";

export const DEFAULT_LOCALE: Locale = "de";
export const SUPPORTED_LOCALES: Locale[] = ["de", "en", "fr", "es"];

export const LOCALE_NAMES: Record<Locale, string> = {
  de: "Deutsch",
  en: "English",
  fr: "Français",
  es: "Español",
};

/**
 * Get localized value with fallback chain:
 * 1. Requested locale
 * 2. Default locale (de)
 * 3. Empty string
 */
export function getLocalizedValue<T = string>(
  localizedField: Localized<T> | undefined,
  locale: Locale = DEFAULT_LOCALE,
  fallback?: T
): T {
  if (!localizedField) {
    return (fallback ?? "") as T;
  }

  // Try requested locale
  if (localizedField[locale]) {
    return localizedField[locale];
  }

  // Fall back to default locale
  if (locale !== DEFAULT_LOCALE && localizedField[DEFAULT_LOCALE]) {
    return localizedField[DEFAULT_LOCALE];
  }

  // Use provided fallback or empty string
  return (fallback ?? "") as T;
}

/**
 * Get localized slug with fallback
 */
export function getLocalizedSlug(
  slugField: Localized<string> | undefined,
  locale: Locale = DEFAULT_LOCALE
): string {
  return getLocalizedValue(slugField, locale, "");
}

/**
 * Check if a locale is supported
 */
export function isValidLocale(locale: string): locale is Locale {
  return SUPPORTED_LOCALES.includes(locale as Locale);
}

/**
 * Get locale from path segment
 */
export function getLocaleFromPath(pathSegment: string): Locale {
  if (isValidLocale(pathSegment)) {
    return pathSegment;
  }
  return DEFAULT_LOCALE;
}

/**
 * Build localized path
 */
export function localizedPath(path: string, locale: Locale = DEFAULT_LOCALE): string {
  // Don't prefix default locale
  if (locale === DEFAULT_LOCALE) {
    return path;
  }

  // Ensure path starts with /
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/${locale}${cleanPath}`;
}

/**
 * Strip locale prefix from path
 */
export function stripLocalePrefix(path: string): { locale: Locale; path: string } {
  const segments = path.split("/").filter(Boolean);

  const firstSegment = segments[0];
  if (firstSegment && isValidLocale(firstSegment)) {
    return {
      locale: firstSegment as Locale,
      path: "/" + segments.slice(1).join("/"),
    };
  }

  return {
    locale: DEFAULT_LOCALE,
    path,
  };
}
